import { Edit2, Trash2, ArrowUpDown, AlertTriangle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { Ingredient } from "@/hooks/useIngredients";

interface IngredientListProps {
  ingredients: Ingredient[];
  isLoading: boolean;
  onEdit: (ingredient: Ingredient) => void;
  onDelete: (id: string) => Promise<boolean>;
  onAddMovement: (ingredient: Ingredient) => void;
}

export function IngredientList({
  ingredients,
  isLoading,
  onEdit,
  onDelete,
  onAddMovement,
}: IngredientListProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const getStockStatus = (ingredient: Ingredient) => {
    if (ingredient.current_stock <= 0) {
      return { label: "Zerado", variant: "destructive" as const };
    }
    if (ingredient.current_stock <= ingredient.minimum_stock) {
      return { label: "Baixo", variant: "secondary" as const };
    }
    return { label: "OK", variant: "default" as const };
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (ingredients.length === 0) {
    return (
      <div className="text-center py-12 border rounded-lg">
        <p className="text-muted-foreground">Nenhum ingrediente cadastrado</p>
        <p className="text-sm text-muted-foreground mt-1">
          Clique em "Novo Ingrediente" para começar
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Ingrediente</TableHead>
            <TableHead className="text-right">Estoque</TableHead>
            <TableHead className="text-right">Mínimo</TableHead>
            <TableHead className="text-right">Custo Unit.</TableHead>
            <TableHead className="text-center">Status</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ingredients.map((ingredient) => {
            const status = getStockStatus(ingredient);
            const isLowStock = ingredient.current_stock <= ingredient.minimum_stock;

            return (
              <TableRow key={ingredient.id} className={isLowStock ? "bg-destructive/5" : ""}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {isLowStock && (
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                    )}
                    <div>
                      <p className="font-medium">{ingredient.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Unidade: {ingredient.unit}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono">
                  {ingredient.current_stock.toFixed(2)} {ingredient.unit}
                </TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">
                  {ingredient.minimum_stock.toFixed(2)} {ingredient.unit}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {formatCurrency(ingredient.cost_per_unit)}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={status.variant}>{status.label}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onAddMovement(ingredient)}
                      title="Movimentar estoque"
                    >
                      <ArrowUpDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(ingredient)}
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" title="Excluir">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir ingrediente?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação não pode ser desfeita. O ingrediente "{ingredient.name}"
                            será removido permanentemente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDelete(ingredient.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
